import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AgGridEvent, ColDef } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { TasksModalComponent } from 'src/app/dashboard/modals/tasks-modal/tasks-modal.component';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { AgentStateDetailComponent } from '../../../../modals/agent-state-detail/agent-state-detail.component';
import { ColorInfo, EState, FOOTER_ROW_DEFAULT_VALUE, ModuleData, ModuleView } from '../../current-agent-states.models';
import { CurrentAgentStatesService } from '../../current-agent-states.service';
import { AgentStateCellRendererComponent } from '../../agent-state-cell-renderer/agent-state-cell-renderer.component';
import { AgGridAngular } from 'ag-grid-angular';
import { AgentStateDetailCXOneComponent } from '../../../../modals/agent-state-detail-cxone/agent-state-detail-cxone.component';
import { ClearViewUtils } from 'src/shared/utils';
import { ContextMenuAwardsDialogComponent } from 'src/app/dashboard/awards/context-menu-awards-dialog/context-menu-awards-dialog.component';
import { SuggestCoachingModalComponent } from '../../../../modals/suggest-coaching-modal/suggest-coaching-modal.component';

@Component({
    selector: 'cv-current-agent-states-pm-table',
    templateUrl: './current-agent-states-pm-table.component.html',
    styleUrls: ['./current-agent-states-pm-table.component.scss']
})
export class CurrentAgentStatesPmTableComponent implements OnInit, OnChanges, OnDestroy {
    @Input() moduleManager: ModuleManager;
    @Input() noData: boolean;
    @Input() sharedData: ModuleData;
    @Input() readOnly: boolean;
    @Input() activeView: ModuleView;
    @Input() showFilter: boolean;
    @Input() _stateToColorMap: ColorInfo;
    @Input() moduleKey: string;

    @ViewChild('agentListWidgetGrid') agGrid: AgGridAngular;
    @ViewChild("filterInput", { read: ElementRef }) filterInput: ElementRef<HTMLInputElement>; // Reference to the <input> element of the filter box.  Used to focus when opened.

    runAfterFirst = false;
    columnVisibleToggle = false;
    modalRef: NgbModalRef = null;
    _stateSaveTimer: NodeJS.Timeout = null;
    filterValue = ""; // Content of the filter box.  Used to filter the table.
    actualFilter = ''; //Value to send to quickFilter, so spaces don't get removed from input.
    gridOptions = null; // set in ngOnInit()
    gridReady = false; // Used to only construct the ag-grid table when ready.
    subscriptions: Subscription[] = [];
    tableState = null;
    tableDisplayItems = [];
    hideTotal = false;
    searchPlaceholder = ""; //For cxone search placeholder localization
    usingDynamicsColumns = false;
    footerRow = {};
    avgFooterRow = {};
    isAgentStateDetailModalOpen = false;
    canSaveNewMetrics = false;
    private hasMetrics = false; // Flag to check if user has enable and selected metrics in the table
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting };
    private isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private agContextMenuService: AgContextMenuService,
        private service: CurrentAgentStatesService,
        private ngbModal: NgbModal,
        private navigationService: NavigationService,
        public config: CvConfigurationService,
    ) { }

    async loadLocalizations() {
        const localizationPromises = [
            this.cvLocalizationService.GetSection("Error_Module"),
            this.cvLocalizationService.GetSection("Common"),
            this.cvLocalizationService.GetSection('Current_Agent_States_Module'),
            this.cvLocalizationService.GetSection('Module_Menu')
        ];
        await Promise.all(localizationPromises);
        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
    }

    async ngOnInit() {
        await this.loadLocalizations();

        this.tableDisplayItems = [ // items for the display menu that uses both radio buttons and checkboxes
            {
                displayName: this.cvLocalizationService.getCachedText("Module_Menu", "Show Totals", "Show Totals"),
                inputType: 'radio',
                value: false,
                checked: this.hideTotal
            },
            {
                displayName: this.cvLocalizationService.getCachedText("Common", "None", "None"),
                inputType: 'radio',
                value: true,
                checked: this.hideTotal
            },
        ];

        this.hasMetrics = this.sharedData?.MetricsAgentStatesColumnValues?.length > 0;

        this.subscriptions.push(this.agContextMenuService.agentDetailsClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey())
                return;
            const response = await this.service.loadCurrentAgentStatesByGroupID(params.moduleKey, params.groupData.groupID);
            this.openAgentStateDetail(params.moduleKey, params.groupData.groupID, response);
        }));
        
        this.subscriptions.push(this.agContextMenuService.createTaskClicked.subscribe(params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openCreateTaskModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));

        this.subscriptions.push(this.agContextMenuService.giveAwardClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }

            this.openAwardModal(params.moduleKey, params.groupData.groupID, params.groupData, this.config.isCxOne)
        }));

        this.subscriptions.push(this.agContextMenuService.suggestCoachingClicked.subscribe(async params => {
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openSuggestCoachingModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));
        
        await Promise.all(this.subscriptions);

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: stateData => {
                if (!stateData) { // No table state loaded
                    return;
                }
                this.tableState = stateData;
                this.restoreTableState();
            },
            autoLoad: true
        });

        this.moduleManager.addState({
            name: "TotalsRow",
            getStateData: () => this.hideTotal,
            onStateLoaded: (state: boolean) => {
                this.hideTotal = state;
                this.updateTotal([this.footerRow]);
                this.tableDisplayItems.filter(i => i.inputType === 'radio').forEach((item) => { item.checked = !!state });
            },
            autoLoad: true,
        });

        
        const selectedGroup = await this.service.getGroupTypeName(this.moduleKey);
        let teamColName = selectedGroup ||
            this.cvLocalizationService.getCachedText('Current_Agent_States_Module', `Team_Header`, 'Team');

        const timeInStateFilterParams = {
            filterOptions: ['contains', 'notContains',
            'equals', 'notEqual', 'startsWith', 'endsWith',
            {
                displayKey: 'greaterThan',
                displayName: 'Greater than',
                predicate: ([filterValue], cellValue) => 
                {
                    const splitTimesFilter = filterValue.split(':');
                    
                    if(splitTimesFilter.length > 3) // not in HH:MM:SS format
                        return null;

                    for(let i = 0; i < splitTimesFilter.length; i++) 
                    {
                        if(splitTimesFilter[i].indexOf('.') > -1) //decimal
                            return null;

                        let firstCharGreaterThanFive = (splitTimesFilter[i][0] > "5" && splitTimesFilter[i].length != 1);
                        let lengthGreaterThanTwo = splitTimesFilter[i].length > 2;
                        let lengthIsZero = (i == 2 && splitTimesFilter[1].length == 0);
                        if(i > 0 && (firstCharGreaterThanFive || lengthGreaterThanTwo || lengthIsZero)) // Check if mins or seconds has first char > 5 or length > 2, making it invalid.
                            return null;
                    }

                    const filteredTotal = this.parseSecondsFromStrings(splitTimesFilter);

                    const splitTimesCell = cellValue.split(':');
                    const valueTotal = this.parseSecondsFromStrings(splitTimesCell);
                    
                    return (cellValue !== null) && (valueTotal > filteredTotal);
                }
            }, 
            ]
        }

        const prefix = '';
        this.gridOptions = this.agGridHelperService.createGridOptions({
            // default column options
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                tooltipValueGetter: params => params.valueFormatted ? params.valueFormatted : params.value,
                icons: null,
                minWidth: this.agGridHelperService.defaultMinWidth,
            },

            // grid options
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            overlayNoRowsTemplate: null,
            components: {
                groupRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
                AgentStateCellRenderer : AgentStateCellRendererComponent,
            },
            context: {
                moduleManager: this.moduleManager,
            },

            // grid data
            columnDefs: [
                {
                    field: 'Group',
                    headerName: this.cvLocalizationService.getCachedText('Current_Agent_States_Module', `${prefix}Name_Header`, 'Name'),
                    colId: 'Name',
                    cellRenderer: 'groupRenderer',
                    type: 'groupColumn',
                    sortable: true,
                    comparator: this.agGridHelperService.groupComparator,
                    filter: 'agTextColumnFilter',
                    tooltipComponent: undefined, // needed to override the custom tooltip used metrics interval and metric summary
                    suppressMenu: this.isSharedURL,
                },
                {
                    field: 'ParentGroupName',
                    // This header will be replaced by the proper group level 2 name after the data loads.
                    headerName: teamColName,
                    colId: 'Team',
                    filter: 'agTextColumnFilter',
                    suppressMenu: this.isSharedURL,
                },
                {
                    field: 'State',
                    headerName: this.cvLocalizationService.getCachedText('Current_Agent_States_Module', `${prefix}State_Header`, 'State'),
                    cellRenderer: 'AgentStateCellRenderer',
                    cellRendererParams: {
                        // must be a function else when the map is updated in the parent it doesn't get updated here
                        getColorMap: () => this._stateToColorMap,
                        isCXOne: this.config.isCxOne
                    },
                    colId: 'State',
                    filter: 'agTextColumnFilter',
                    // Since the colors can be updated by the user, the color needs to be part of the value, so that Ag-Grid sees the changes and refreshes the cells.
                    valueGetter: params => { return params.data.State.DisplayState; },
                    tooltipValueGetter: params => params.data.State.DisplayState,
                    getQuickFilterText: params => params.data.State.DisplayState,
                    // Custom comparator is necessary, since the valueGetter prepends the color code, which messes with the sort.
                    comparator: (valueA, valueB, nodeA, nodeB) => {
                        let agentStateA = null;
                        let agentStateB = null;
                        if (nodeA && nodeA.data && nodeA.data.State)
                            agentStateA = nodeA.data.State.DisplayState;
                        if (nodeB && nodeB.data && nodeB.data.State)
                            agentStateB = nodeB.data.State.DisplayState;

                        if (!agentStateA && !agentStateB)
                            return 0;
                        else if (!agentStateA && agentStateB)
                            return -1;
                        else if (agentStateA && !agentStateB)
                            return 1;
                        else
                            return agentStateA.localeCompare(agentStateB, undefined, { numeric: true, sensitivity: "base" });
                    },
                    suppressMenu: this.isSharedURL,
                },
                {
                    field: 'Skill',
                    headerName: this.cvLocalizationService.getCachedText('Current_Agent_States_Module', `${prefix}Source_Header`, 'Source'),
                    colId: 'Skill',
                    filter: 'agTextColumnFilter',
                    valueGetter: params => {
                        const skillsAssigned = params.data[params.colDef.field];
                        let count = 0;
                        if (skillsAssigned != null && Array.isArray(skillsAssigned)) {
                            count = skillsAssigned.length;
                        }

                        switch (count) {
                            case 0:
                                const skill = "-";
                                return skill;
                            case 1:
                                return skillsAssigned[0];
                            default:
                                return count + " " + this.cvLocalizationService
                                    .getCachedText('Current_Agent_States_Module', "Skills_Assigned_Plural", "Assigned");
                        }
                    },
                    tooltipValueGetter: params => {
                        const skillsList = params.data[(params.colDef as ColDef).field];
                        if (skillsList != null && Array.isArray(skillsList) && skillsList.length > 0)
                            return skillsList.join(", ");
                        else
                            return null;
                    },
                    comparator: (valueA, valueB) => {
                        if (typeof valueA === "string" && typeof valueB === "string") {
                            return valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: "base" });
                        } else {
                            if (valueA == null && valueB == null)
                                return 0;
                            else if (valueA != null && valueB == null)
                                return 1;
                            else if (valueA == null && valueB != null)
                                return -1;
                            else
                                return valueA - valueB;
                        }
                    },
                    suppressMenu: this.isSharedURL,
                },
                {
                    field: 'Duration',
                    headerName: this.cvLocalizationService.getCachedText('Current_Agent_States_Module', 'Duration', 'Time In State'),
                    colId: 'Duration',
                    cellClassRules: {
                        highlightDurationCell : params => params.data.HighlightValue
                    },
                    filter: 'agTextColumnFilter',
                    filterParams: timeInStateFilterParams,
                    suppressMenu: this.isSharedURL,
                },
            ],
            rowData: [],
            // external filtering is done through the quickFilter.
            // grid events
            onGridReady: event => {
                this.gridReady = true;
                this.updateColumnDefs();
                if (this.sharedData && this.sharedData.RowValues && this.sharedData.RowValues.length > 0) {
                    this.groupStateTypesByDefault();
                    this.loadTableData(this.sharedData.RowValues);
                }

                this.stateRestoreInProgress = true;
                this.restoreTableState(); //Needed to restore columns size saved on the tableState.
                if (!this.tableState) {
                    this.sizeColumns(event); //This will trigger the publish bar only once if the tableState is null.
                    this.queueStateSave();
                }

                this.constructContextMenuItems();
                this.canSaveNewMetrics = true;

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Let events catch up
            },
            getRowId: ({ data: rowData }) => rowData.Group.groupID,
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.sizeColumns(event);
                    this.queueStateSave();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreTableState();
                }

                // needed to add menu item when moving from dual to table or visa versa
                this.constructContextMenuItems();
            },
            onSortChanged: () => this.queueStateSave(),
            onColumnMoved: () => this.queueStateSave(),
            onColumnPinned: () => this.queueStateSave(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized") {
                    this.queueStateSave();
                }
            },
            onColumnVisible: event => {
                this.columnVisibleToggle = true;
                this.queueStateSave();
                if (this.runAfterFirst) {
                    this.sizeColumns(event);
                }
                this.runAfterFirst = true;
                this.columnVisibleToggle = false;
            },
            onFilterChanged: () => this.queueStateSave(),
            localeText: await this.agGridHelperService.getLocalizations(),
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
        },
        {
            enableCopy: true
        }
        );

        if (this.readOnly) {
            this.agGridHelperService.setGridReadOnly(this.gridOptions);
        }
    }

    /** Opens the Agent State Detail modal from the Context Menu on Groups column */
    openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any) {
        if (this.isAgentStateDetailModalOpen) return;
        this.isAgentStateDetailModalOpen = true;
        this.navigationService.showOverlay = true;

        this.modalRef = this.ngbModal.open(AgentStateDetailComponent,
            { size: "xl", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => {
            this.navigationService.showOverlay = false;
            this.isAgentStateDetailModalOpen = false;
        });

        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openCreateTaskModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TasksModalComponent,
            {size : "xl", centered: true, scrollable: true, modalDialogClass: null});
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: TasksModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openAwardModal(moduleKey: string, groupID: number, selectedRow: any, _isCxone: boolean) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(ContextMenuAwardsDialogComponent,
            { size: "xl", centered: true, scrollable: true, modalDialogClass: 'give-award-dialog' });
        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
    }

    openSuggestCoachingModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(SuggestCoachingModalComponent,
            { size: "md", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: SuggestCoachingModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    /** this function will ensure that all states with the same color code are grouped by each other in the
     * table. this is the order by which they will be grouped availables, loggedOut, inbound, outbound, mixed, unavailable, custom, not found in color system
     * the only potential issue here would be if they created custom states of multiple categories since they all would share the same custom state code
     */
    groupStateTypesByDefault() {
        const availables = this.sharedData.RowValues.filter(row => row.State.State === EState.Available);
        const loggedOut = this.sharedData.RowValues.filter(row => row.State.State === EState.LoggedOut);
        const inbound = this.sharedData.RowValues.filter(row => row.State.State === EState.Inbound);
        const outbound = this.sharedData.RowValues.filter(row => row.State.State === EState.Outbound);

        // Group unavailable, custom, and mixed categories together
        const mixedGroup = [
            ...this.sharedData.RowValues.filter(row => row.State.State === EState.Mixed),
            ...this.sharedData.RowValues.filter(row => row.State.State === EState.Unavailable),
            ...this.sharedData.RowValues.filter(row => row.State.State === EState.Custom)
        ];
        this.alphabetizeRows(availables, loggedOut, inbound, outbound, mixedGroup);

        const grouped = [...availables, ...loggedOut, ...inbound, ...outbound, ...mixedGroup];

        // find a way to get the rest that dont belong in any group
        const notInSystem = this.sharedData.RowValues.filter(row => !grouped.find(rowG => row.State.State === rowG.State.State));
        this.alphabetizeRows(notInSystem);

        this.sharedData.RowValues = [...grouped, ...notInSystem];
    }

    alphabetizeRows(...arrays) {
        return arrays.map(array => array.sort((a, b) => (a.State?.DisplayState ?? "").localeCompare(b.State?.DisplayState ?? "")));
    }

    // Event handler when the parent says the data changed.
    onDataChanged() {
        this.groupStateTypesByDefault();
        if (!this.gridReady) return;
        this.updateColumnDefs();
        this.loadTableData(this.sharedData.RowValues);
    };

    loadTableData(data) {
        if (!this.gridOptions) return;

        data.forEach((row) => {
            const standardizedKey = ClearViewUtils.standardizeLocalizationKey(row.State.DisplayState);
            row.State.DisplayState = this.cvLocalizationService
                .getCachedText("Current_Agent_States_Module", standardizedKey, row.State.DisplayState);
        });

        if (!this.usingDynamicsColumns) {
            if (data && data.length > 0) {
                this.agGrid?.api.setGridOption('rowData', data);
                this.agGrid?.api.refreshCells();
            }
            return;
        }

        const rowData = data.map(row => {
            const newRow = { ...row };
            if (row.metricData && Array.isArray(row.metricData)) {
                row.metricData.forEach(metric => {
                    if (metric) {
                        newRow[`Metric${metric.MetricID}`] = metric.MetricDisplayValue;
                    }
                });
            }
            return newRow;
        });

        if (rowData.length > 0) {
            this.agGrid?.api.setGridOption('rowData', rowData);
            this.agGrid?.api.refreshCells();
        }

        this.restoreTableState();
    }

    columnAlreadyInDefs(columnDefs: ColDef[], column: ColDef) {
        return columnDefs.findIndex(colDef => colDef.field === column.field) !== -1;
    };

    updateGridTable(gridOptions, colummsDefs, footerRow, avgFooterRow, restoreTableState = false) {
        this.updateTotal(footerRow);
        this.agGrid?.api.setGridOption('columnDefs', colummsDefs);
        this.agGrid?.api.refreshHeader();
        this.agGrid?.api.refreshCells({ force: true });
        if (restoreTableState && this.tableState)
            this.restoreTableState();
        else
            this.sizeColumns(null);
    };

    insertDynamicColumns(newDynamicColumnDefs) {
        const parentGroupNameIndex = this.gridOptions.columnDefs.findIndex(col => col.field === 'ParentGroupName');
        if (parentGroupNameIndex !== -1) {
            const stateIndex = this.gridOptions.columnDefs.findIndex(col => col.field === 'State');
            if (stateIndex !== -1) {
                this.gridOptions.columnDefs.splice(stateIndex, 0, ...newDynamicColumnDefs);
            }
        }
    }

    removeOldDynamicColumns(previousDynamicColDefs, dynamicColumnDefs) {
        let columnsDeleted = false;
        for (const colDef of previousDynamicColDefs) {
            const found = dynamicColumnDefs.find(dynamicColDef => dynamicColDef.field === colDef.field);
            if (!found) {
                const index = this.gridOptions.columnDefs.findIndex(c => c.field === colDef.field);
                this.gridOptions.columnDefs.splice(index, 1);
                columnsDeleted = true;
            }
        }
        return columnsDeleted;
    }

    getDefaultColumnDefs(regex) {
        return this.gridOptions.columnDefs.filter(colDef => colDef.field.match(regex));
    }

    getDynamicColumnDefs(regex) {
        return this.gridOptions.columnDefs.filter(colDef => !colDef.field.match(regex));
    }

    updateColumnDefs() {
        if (!this.shouldUpdateColumnDefs()) return;

        const isNotMetricColumnRegex = /^(?!Metric\d+)\w+/; // regex to match all columns that are not metrics
        if (this.handleDefaultColumnDefs(isNotMetricColumnRegex)) {
            return;
        }
        this.usingDynamicsColumns = true;

        const dynamicColumnDefs = this.generateDynamicColumnDefs();
        const newDynamicColumnDefs = this.findNewDynamicColumnDefs(dynamicColumnDefs);
        const previousDynamicColDefs = this.getDynamicColumnDefs(isNotMetricColumnRegex);

        let columnsDeleted = false;
        columnsDeleted = this.removeOldDynamicColumns(previousDynamicColDefs, dynamicColumnDefs);

        if (newDynamicColumnDefs.length > 0) {
            this.insertDynamicColumns(newDynamicColumnDefs);
        }

        const [footerRow, avgFooterRow, footerChanged] = this.updateFooterRow(this.sharedData.MetricsAgentStatesColumnValues);
        let columnDefs = this.updateColumnDefsFooterValues(footerRow, avgFooterRow, isNotMetricColumnRegex);
        const columnOrderChange = this.detectColumnOrderChanges(columnDefs);
        const headerNameChanged = this.detectHeaderNameChange(previousDynamicColDefs, dynamicColumnDefs);

        if(headerNameChanged) {
            columnDefs = this.updateColumnHeaderNames(columnDefs, dynamicColumnDefs);
        }

        if (footerChanged) {
            this.updateGrid(columnDefs, footerRow, avgFooterRow);
        }

        if (newDynamicColumnDefs.length > 0 || columnOrderChange > 0 || columnsDeleted || headerNameChanged) {
            this.updateGrid(columnDefs, footerRow, avgFooterRow);
            if (this.canSaveNewMetrics) {
                this.prepareTableState();
                this.queueStateSave();
            }
        }
    }

    shouldUpdateColumnDefs() {
        if (!this.gridReady) return false;
        if (!this.usingDynamicsColumns && this.sharedData.MetricsAgentStatesColumnValues?.length === 0) return false;
        return true;
    }

    handleDefaultColumnDefs(isNotMetricColumnRegex) {
        if (this.sharedData.MetricsAgentStatesColumnValues?.length === 0) {
            const filteredColumnDefs = this.getDefaultColumnDefs(isNotMetricColumnRegex);
            if (filteredColumnDefs === this.gridOptions.columnDefs) {
                return true;
            }
            this.gridOptions.columnDefs = filteredColumnDefs;
            this.usingDynamicsColumns = false;
            this.footerRow = {};
            this.avgFooterRow = {};
            if (this.agGrid?.api) {
                this.updateGridTable(this.gridOptions, this.gridOptions.columnDefs, false, false);
                this.prepareTableState();
                this.queueStateSave();
            }
            return true;
        }
        return false;
    }

    generateDynamicColumnDefs() {
        const dynamicColumnDefs = [];
        for (const ugColDef of this.sharedData.MetricsAgentStatesColumnValues) {
            const isRenamed = ugColDef.customName && ugColDef.customName !== "";
            const agColDef = {
                headerName: isRenamed ? ugColDef.customName + "*" : ugColDef.displayName,
                field: ugColDef.name,
                colId: ugColDef.displayName,
                filter: 'agTextColumnFilter',
                headerTooltip: isRenamed ? 
                this.cvLocalizationService.getCachedText("Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + ugColDef.displayName : null,
                suppressMenu: this.isSharedURL,
            };
            dynamicColumnDefs.push(agColDef);
        }
        return dynamicColumnDefs;
    }

    findNewDynamicColumnDefs(dynamicColumnDefs) {
        const newDynamicColumnDefs = [];
        for (const agColDef of dynamicColumnDefs) {
            if (!this.columnAlreadyInDefs(this.gridOptions.columnDefs, agColDef)) {
                newDynamicColumnDefs.push(agColDef);
            }
        }
        return newDynamicColumnDefs;
    }

    detectHeaderNameChange(previousDynamicColDefs, dynamicColumnDefs) {
        const b = dynamicColumnDefs.some((colDef) => previousDynamicColDefs.some((prevColDef) => colDef.field === prevColDef.field && colDef.headerName !== prevColDef.headerName));
        return b
    }

    updateFooterRow(dynamicColumnDefs): [object, object, boolean] {
        let footerChanged = false;

        const footerRow = {
            ID: 'total',
            Group: {
                groupID: 'total',
                groupName: this.cvLocalizationService.getCachedText('Current_Agent_States_Module', 'Totals', 'Totals')
            },
        };
        for (const agColDef of dynamicColumnDefs) {
            if (Object.keys(this.footerRow).length > 0) {
                if (this.footerRow[agColDef.name] !== agColDef.total) {
                    footerChanged = true;
                }
            }
            footerRow[agColDef.name] = agColDef.total;
        }

        const avgFooterRow = {
            ID: 'average',
            Group: {
                groupID: 'average',
                groupName: this.cvLocalizationService.getCachedText('Current_Agent_States_Module', 'Averages', 'Averages'),
            },
        };
        for (const agColDef of dynamicColumnDefs) {
            if (Object.keys(this.avgFooterRow).length > 0) {
                if (this.avgFooterRow[agColDef.name] !== agColDef.avg) {
                    footerChanged = true;
                }
            }
            avgFooterRow[agColDef.name] = agColDef.avg;
        }
        return [footerRow, avgFooterRow, footerChanged];
    }

    updateColumnDefsFooterValues(footerRow, avgFooterRow, isNotMetricColumnRegex) {
        const columnDefs = [...this.gridOptions.columnDefs];
        for (const colDef of columnDefs) {
            if (colDef.field.match(isNotMetricColumnRegex) && colDef.field !== 'Group') {
                if (colDef.field === 'State') {
                    footerRow[colDef.field] = {
                        DisplayState: FOOTER_ROW_DEFAULT_VALUE,
                        State: EState.Unavailable,
                    };
                    avgFooterRow[colDef.field] = {
                        DisplayState: FOOTER_ROW_DEFAULT_VALUE,
                        State: EState.Unavailable,
                    };
                } else {
                    footerRow[colDef.field] = FOOTER_ROW_DEFAULT_VALUE;
                    avgFooterRow[colDef.field] = FOOTER_ROW_DEFAULT_VALUE;
                }
            }
        }
        this.footerRow = footerRow;
        this.avgFooterRow = [];
        return columnDefs;
    }

    updateColumnHeaderNames(columnDefs, dynamicColumnDefs) {
        for (let i = 0; i < columnDefs.length; i++) {
            const columnMatch = dynamicColumnDefs.find(c => c.field === columnDefs[i].field);
            if (columnMatch && columnDefs[i].headerName !== columnMatch.headerName) {
                columnDefs[i].headerName = columnMatch.headerName;
            }
        }
        return columnDefs;
    }

    detectColumnOrderChanges(columnDefs) {
        let columnOrderChange = 0;
        if (columnDefs.length === this.gridOptions.columnDefs.length) {
            for (let position = 2; position < this.gridOptions.columnDefs.length; position++) {
                if (columnDefs[position].headerName !== this.gridOptions.columnDefs[position].headerName) {
                    columnOrderChange++;
                }
            }
        }
        return columnOrderChange;
    }

    updateGrid(columnDefs, footerRow, avgFooterRow) {
        this.gridOptions.columnDefs = columnDefs;
        if (this.agGrid?.api) {
            this.updateGridTable(this.gridOptions, this.gridOptions.columnDefs, [footerRow], [avgFooterRow], true);
        }
    }


    constructContextMenuItems() {
        if (this.agGrid?.api)
            this.addContextMenuItems();
        else
            console.error("CurrentAgentStatesTable: constructColumnMenuItem() is being called outside of gridOptions");
    };

    // Function called when the menu item is checked/unchecked to hide/show columns.
    toggleColumnVisible(i: number) {
        const column: ColDef = this.gridOptions.columnDefs[i]; //column clicked
        const tableStateMatch = this.tableState.columns.find(c => c.name === column.colId); //column on table
        if (tableStateMatch) {
            column.hide = !tableStateMatch.visible;
        }
        const isHidden = !tableStateMatch.visible;

        // toggle column visibility
        this.agGrid?.api.setColumnVisible(tableStateMatch.name, isHidden);

        // update table state immediately to prevent UI delay (instead of queuing)
        this.prepareTableState();
        this.queueStateSave();
    }

    prepareTableState() {
        if (!this.agGrid?.api)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid.api);
        if (tableState != null) {
            this.tableState = tableState;
        }
    }

    addContextMenuItems() {
        this.moduleManager.addMenuItem({
            name: "Columns",
            icon: "columns",
            getEnabled: () => this.gridOptions.columnDefs.length > 0,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.gridOptions.columnDefs,
            getItemName: i =>
            {
                const colState = this.gridOptions.columnDefs[i];
                const column = this.agGrid?.api.getColumn(colState.colId);

                if (column) // during column changes the state can have columns that are no longer in the list
                    return column['colDef'].headerName;
                else
                    return "";
            },
            isItemChecked: i => {
                if (this.gridReady && !this.tableState) {
                    this.prepareTableState();
                }
                const columnDefinition: ColDef = this.gridOptions.columnDefs[i];

                // Since the index values may be out of order if columns are reordered,
                // find the correct table state by name/field instead of relying on the index.
                const tableStateMatch = this.tableState.columns.find(c => c.name === columnDefinition.colId);
                if (tableStateMatch) {
                    columnDefinition.hide = !tableStateMatch.visible;
                }
                return !columnDefinition.hide;
            },
            onCheckClicked: i => this.toggleColumnVisible(i),
        } as IModuleManagerMenuItem);

        this.moduleManager.addMenuItem({
            name: "Table Display", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
            icon: "tableDisplay",
            getEnabled: () => this.hasMetrics,
            type: ModuleManagerMenuItemType.RadioCheckMenu,
            getCheckItems: () => this.tableDisplayItems,
            getItemName: i => this.tableDisplayItems[i].displayName,
            getItemInputType: i => { return i <= this.tableDisplayItems.length - 1 ? this.tableDisplayItems[i]?.inputType : 'checkbox' },
            isItemChecked: i => this.tableDisplayItems[i].checked,
            onCheckClicked: (i) => {
                if (this.tableDisplayItems[i].inputType === 'radio') {
                    this.hideTotal = this.tableDisplayItems[i].value;
                    this.tableDisplayItems.filter(i => i.inputType === 'radio').forEach((item) => { item.checked = this.hideTotal });
                    this.updateTotal([this.footerRow]);
                }
                else {
                    this.tableDisplayItems[i].checked = !this.tableDisplayItems[i].checked;
                }
                this.queueStateSave();
                this.moduleManager.requestPublishNeeded();
            },
            readOnlyEnabled: true
        } as IModuleManagerMenuItem);
    }

    // Called by clearFilter -> toggleShowFilter when angular notices a change on the showFilter variable from the parent component
    applyFilter() {
        if (this.agGrid?.api !== null) { // notify ag-grid that the filter changed
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue.trim());
            if(this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0){
                this.agGrid?.api?.showNoRowsOverlay();
            }
            else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    // Called when the clear button on the filter box is clicked.
    clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    };

    // Shows/hides the filter box
    toggleShowFilter() {
        if (this.showFilter){
            this.showFilter = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        }
        else {
            this.showFilter = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnFilter();
        }
    };

    focusOnFilter() {
        setTimeout(() => {
            this.filterInput.nativeElement.focus();
        }, 200);
    }

    // Called by some of ag-grid's events
    sizeColumns(event: AgGridEvent) {
        if (!this.sharedData) {
            return;
        }

        // helper function to size columns
        if (event)
            this.agGridHelperService.sizeColumnsSmart(event);
        else
            this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api);
    };

    // Debounces state save before emitting event to parent to save the state
    queueStateSave() {
        if (this.stateRestoreInProgress)
            return; // Don't save while a state restore is happening

        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this._stateSaveTimer) {
            clearTimeout(this._stateSaveTimer);
            this._stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this._stateSaveTimer = setTimeout(async () => {
            this._stateSaveTimer = null;
            if (this.agGrid?.api)
                this.tableState = this.agGridHelperService.getGridState(this.agGrid.api);
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500/*ms*/);
    }

    // Event handler when the parent says the table state has been restored
    restoreTableState() {
        // If the moduleManager restores the state after this directive is constructed, restore it here.
        if (this.tableState && this.tableState.columns && this.agGrid?.api) {
            this.agGridHelperService.loadGridState(this.agGrid.api, this.tableState);
        }
    };

    // Assumes that string array will follow order HH, MM, SS
    // and that the format is correct
    parseSecondsFromStrings(parsedTimes: string[]): number {
        let totalSeconds = 0;
        for(let i =0; i < parsedTimes.length ; i++)
        {
            const n: number = +parsedTimes[i];
            totalSeconds += (Math.pow(60, (2-i))) * n;
        }

        return totalSeconds;
    }

    // when values change from parent component this will trigger responses in this component
    ngOnChanges(changes: SimpleChanges): void {
        if (changes["showFilter"] && this.gridOptions !== null) {
            this.toggleShowFilter();
            this.clearFilter();
        }
        if(changes["sharedData"]) {
            this.onDataChanged();
        }
        if(changes["sharedData"] && this.filterValue.length !== 0 && this.agGrid?.api?.getDisplayedRowCount() === 0) {
            this.applyFilter();
        }
        if(changes["_stateToColorMap"]) {
            // if force isn't declared it won't refresh unless the state value changes, since we are changing the color we need force
            this.agGrid?.api?.refreshCells({columns: [ 'State' ], force: true});
        }
    }

    updateTotal(footerRow): void {
        if (!this.agGrid?.api) return;

        if (this.hideTotal) {
            this.agGrid?.api.setGridOption('pinnedBottomRowData', []);
            return;
        }

        this.agGrid?.api.setGridOption('pinnedBottomRowData', footerRow);
    }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        this.modalRef = null;
        this.subscriptions.forEach(sub => {
            sub.unsubscribe();
        });
    }
}
